import { Inject, Injectable } from '@angular/core';
import { ConfigService } from '../../config/config.service';
import { LocalStorageService } from '../../core/service/local-storage.service';
import { TranslateService } from '@ngx-translate/core';
import { Language } from '../../config/model/app-config.model';
import {SbbLanguageServiceInterface} from '@sbb-amsgis/sbb-info-banner';

/**
 * @copyright Schweizerische Bundesbahnen SBB
 * @class WidgetNotifyService
 * @version 0.0.1
 * @since September 2017
 * @author Tuan La
 * @description The WidgetNotifyService provides subjects for widget handling
 * and can be used by components and services
 */
@Injectable({
  providedIn: 'root',
})
export class LanguageService implements SbbLanguageServiceInterface {
  private _languageList: Language[];
  private _selectedLanguage: Language;
  private _defaultLanguage: Language;

  constructor(
    @Inject(ConfigService) private _configService: ConfigService,
    @Inject(LocalStorageService) private _localStorageService: LocalStorageService,
    @Inject(TranslateService) private _translate: TranslateService
  ) {
    // set language list and default
    this._languageList = this._configService.appConfig.language;
    for (let i = 0; i < this._languageList.length; i++) {
      if (this._languageList[i].default) {
        this._defaultLanguage = this._languageList[i];
        this._translate.setDefaultLang(this._defaultLanguage.lang);
      }
    }

    // Load all translationFiles from json via config because on deployed server
    // webpack still cant reference correct to translate files
    // TODO : This implementation should be changed as soon as we have a proper solution
    this._translate.setTranslation('de', this._configService.translationFiles.de);
    this._translate.setTranslation('fr', this._configService.translationFiles.fr);
    this._translate.setTranslation('it', this._configService.translationFiles.it);

    this.useStoredLanguage();
  }

  public useStoredLanguage() {
    // check if language-data in local storage are available
    let language = this._localStorageService.language;
    if (!language) {
      language = this._defaultLanguage;
    }

    this._translate.use(language.lang);
    this._selectedLanguage = language;
  }

  public get defaultLanguage() {
    return this._defaultLanguage;
  }

  public get languageList() {
    return this._languageList;
  }

  public get selectedLanguage() {
    return this._selectedLanguage ?? this._defaultLanguage;
  }

  public changeLanguage(lang: Language) {
    this._selectedLanguage = lang;
    this._translate.use(this._selectedLanguage.lang);
    this._localStorageService.language = this.selectedLanguage;
  }
}
